<template>
  <component :is="layout">
    <router-view />
  </component>
  <NewUserModal v-if="showModal" :show="showModal" @close="handleCloseModal" @save="handleSaveModal" />
  <!-- Global full-page loader overlay -->
  <!-- <FullPageLoader :visible="isRouteLoading" /> -->
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from 'vue';
import NewUserModal from '@/components/modals/firstTimeModal.vue';
import { io } from 'socket.io-client';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';

import { applyScheme, getSavedScheme, getSystemScheme, initSystemSchemeListener } from '@/utils/colorSchemeUtils.js';
import { getUserFriendlyErrorMessage } from '@/utils/errorHandler';
import { enableScroll } from '@/utils/scrollControl.js';
import FullPageLoader from '@/components/FullPageLoader.vue';

import defaultLayout from './layouts/default.vue';
import guestLayout from './layouts/guest.vue';
const store = useStore();
const logOut = payload => store.dispatch('logOut', payload);
const addAuthStatus = status => store.dispatch('addAuthStatus', status);
const getReportPost = id => store.dispatch('fetchReportBotId', id);
const handleSetProfile = data => store.dispatch('fetchCurrentProfileOrg', data);
const userdata = JSON.parse(localStorage.getItem('user'));
const user = computed(() => store.getters.getUser);
const isUserAuth = computed(() => store.getters.getAuthStatus);

const route = useRoute();
const router = useRouter();
// --------------------
// Modal state
// --------------------s
const showModal = ref(false);
const userId = computed(() => store.getters.getUser?.id);

const token = computed(() => store.getters.getToken);

const layout = computed(() => {
  const layout = route.meta.layout;
  return layout === 'default' ? defaultLayout : guestLayout;
});

// Global route loader visibility from store
const isRouteLoading = computed(() => store.getters.getIsRouteLoading);


let unregisterSystemSchemeListener;
const ENABLE_SOCKET = String(import.meta.env.VITE_ENABLE_SOCKET || '').toLowerCase() === 'true';
const SOCKET_URL = import.meta.env.VITE_SOCKET_URL || '/';
// --------------------
// Show modal if user is new
// --------------------
const checkPopupStatus = async () => {
  if (!userId.value) return;

  try {
    const res = await fetch(`/api/users/${userId.value}/popup-status`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token.value}`
      }
    });
    const data = await res.json(); // { hasSeenPopup: boolean }
    if (data?.hasSeenPopup === false) {
      showModal.value = true;
    }
  } catch (err) {
    console.error("Popup status check failed:", err);
  }
};
// --------------------
// Close modal
// --------------------
const handleCloseModal = () => {
  showModal.value = false;
};
// Close modal
const handleSaveModal = async () => {
  try {
    const res = await fetch(`/api/users/${userId.value}/popup-seen`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token.value}`
      }
    });

    if (!res.ok) throw new Error("Failed to update popup status");

    showModal.value = false; // hide popup
  } catch (err) {
    console.error("Error saving popup state:", err);
  }
};
window.addEventListener("user-logged-in", () => {
  console.log("User logged in → checking popup...");
  setTimeout(() => {
    checkPopupStatus();
  }, 400);
});
const useSocketIO = () => {
  const socket = io(SOCKET_URL, {
    transports: ['websocket'],
  });

  socket.on('connect', () => {
    console.log('✅ WebSocket connected successfully');
  });

  socket.on('disconnect', () => {
    console.log('❌ WebSocket disconnected');
    // Could show a subtle notification to user about limited functionality
  });

  socket.on('connect_error', (error) => {
    const friendlyMessage = getUserFriendlyErrorMessage(error);
    console.error('❌ WebSocket connection error:', friendlyMessage);

    // In development, also log the original error for debugging
    if (import.meta.env.MODE === 'development') {
      console.error('Original WebSocket error:', error);
    }
  });

  socket.on('post_marked_bot', payload => {
    getReportPost(payload.postId);
  });

  socket.on('new_notification', async () => {
    console.log('🔔 New notification received via WebSocket');

    // Fetch updated unread notifications count
    await store.dispatch('fetchUnreadNotificationsCount');

    // Optionally fetch new unread notifications to update the list
    // Only do this if user is on notifications page to avoid unnecessary API calls
    const currentRoute = route.path;
    if (currentRoute === '/notifications') {
      console.log('📱 User is on notifications page, refreshing notifications list');
      await store.dispatch('fetchNotifications', { read: false, limit: 10, offset: 0 });
    }
  });
};

onMounted(async () => {
  try {
    // Skip initial getMe call if we're on SIWF callback route
    // The callback will handle authentication and user data loading
    const isSiwfCallback = route.path === '/siwf/callback' || route.name === 'siwf-callback';

    if (!isSiwfCallback) {

      let userdata = await store.dispatch('getMe');
      handleSetProfile(userdata);
      applyScheme(getSavedScheme() || getSystemScheme());
      unregisterSystemSchemeListener = initSystemSchemeListener();

      // If user is authenticated, set up notifications and WebSocket
      if (userdata) {
        store.dispatch('startGlobalTimer');
        // if (userId.value) {
        //   await checkPopupStatus();
        // }
        store.dispatch('fetchReputationCategories');
        checkPopupStatus();
        store.dispatch('fetchUnreadNotificationsCount');
        if (ENABLE_SOCKET) {
          useSocketIO();
        }
      }
    } else {
      // For SIWF callback, just set up the color scheme
      applyScheme(getSavedScheme() || getSystemScheme());
      unregisterSystemSchemeListener = initSystemSchemeListener();
      console.log('Skipping getMe call - SIWF callback in progress');
    }
  } catch (error) {
    console.warn(error);
  }
});

onUnmounted(() => {
  if (unregisterSystemSchemeListener) {
    unregisterSystemSchemeListener();
  }
});
// --------------------
// On Mounted
// --------------------

// Ensure body scroll state is sane when navigating to guest pages (e.g., /welcome, /sign-in)
router.afterEach((to) => {
  if (to?.meta && to.meta.layout === 'guest') {
    try {
      enableScroll();
      document.body.classList.remove('modal-open');
      document.body.classList.remove('reactions-modal-open');
      document.body.style.removeProperty('--scrollbar-width');
      // Clear any lingering overflow styles that might interfere with BaseModal
      document.body.style.removeProperty('overflow');
      delete document.body.dataset.prevOverflow;
      // Also clean up root-level modal classes
      document.documentElement.classList.remove('modal-open-root');
    } catch { }
  }
});
</script>

<style>
/* Global layout variables for consistent sticky/fixed offsets */
:root {
  --header-height: 80px;
  --bottom-nav-height: 64px;
  /* mobile bottom nav */
}

/* Prevent scroll chaining to the browser window for all main content scroll containers */
.posts-scroll-container,
.centerbar-posts {
  overscroll-behavior: contain;
  /* When container hits top/bottom, don't scroll the page */
}

@media (max-width: 639px) {

  .centerbar-body,
  .primary__content,
  .stable-router-view {
    /* Add bottom padding equal to nav height + safe-area inset */
    padding-bottom: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom, 0px));
  }

  /* Ensure scrollable content containers also account for the fixed bottom nav */
  .posts-scroll-container,
  .centerbar-posts {
    /*  padding-bottom: calc(var(--bottom-nav-height) + env(safe-area-inset-bottom, 0px) + 16px); */
  }
}
</style>
